/* ===========================================================
   Tendência dos KPIs — função PURA que distribui o agregado do PERÍODO SELECIONADO
   (Total em vendas, Total dos Custos, Marketing, Lucro Gerado — vindo de profit-period,
   idêntico aos cards) sobre os buckets do período (diário até 14 dias; semanal além),
   e alinha o período anterior (comparação) com deltas. Reflete o filtro existente da
   tela — não tem filtro próprio. Consumida pelo gráfico do app/inicio (chart-kpis).
   A soma dos buckets fecha EXATAMENTE no agregado do período (invariante no teste).

   Sem mídia conectada (#2): o agregado já vem sem anúncio (Marketing 0, Lucro = MC)
   de profit-period; aqui só propagamos.
   =========================================================== */
import type { PeriodoLucro, PeriodoRef, WaterfallStep } from "../api/types";
import { relativeDelta } from "./delta";
import { profitPeriod, type AdsSplit, type ProfitPeriod } from "./profit-period";

export interface KpiValores {
  vendas: number;
  custos: number;
  marketing: number;
  lucro: number;
}

export interface BucketKpi extends KpiValores {
  rotulo: string;
  /** Valores do período anterior alinhados por índice (só quando há comparação). */
  anterior: KpiValores | null;
  deltas: {
    vendas: number | null;
    custos: number | null;
    marketing: number | null;
    lucro: number | null;
  } | null;
}

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function rotuloDia(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d ?? ""}/${MESES[Number(m) - 1] ?? ""}`;
}
function addDias(iso: string, n: number): string {
  return new Date(Date.parse(iso) + n * 86400000).toISOString().slice(0, 10);
}
function diasEntre(a: string, b: string): number {
  return Math.round((Date.parse(b) - Date.parse(a)) / 86400000) + 1;
}

/** Rótulos dos buckets do período: diário até 14 dias; semanal além. */
function bucketsDoPeriodo(range: { start: string; end: string }): string[] {
  const dias = Math.max(1, diasEntre(range.start, range.end));
  if (dias <= 14) return Array.from({ length: dias }, (_, i) => rotuloDia(addDias(range.start, i)));
  const nSem = Math.ceil(dias / 7);
  return Array.from({ length: nSem }, (_, i) => rotuloDia(addDias(range.start, i * 7)));
}

/** Distribui um total por `n` buckets com um perfil determinístico; o resíduo cai no
 *  último bucket, então a soma fecha EXATAMENTE no total. */
function distribuir(total: number, n: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [total];
  const pesos = Array.from({ length: n }, (_, i) =>
    Math.max(0.3, 1 + 0.22 * Math.sin(i * 1.3 + 0.5) + 0.12 * Math.cos(i * 0.8)),
  );
  const somaP = pesos.reduce((a, b) => a + b, 0);
  const vals = pesos.map((p) => Math.round((total * p) / somaP));
  const last = n - 1;
  vals[last] = (vals[last] ?? 0) + (total - vals.reduce((a, b) => a + b, 0));
  return vals;
}

function aggDe(pp: ProfitPeriod, mediaConn: boolean): KpiValores {
  return {
    vendas: pp.vendasBrutas,
    custos: pp.custos.total,
    marketing: mediaConn ? pp.marketing.total : 0,
    lucro: pp.lucroExibido,
  };
}

export function kpiTrend({
  waterfall,
  ads,
  periodo,
  anterior,
  mediaConn,
  range,
}: {
  waterfall: WaterfallStep[];
  ads: AdsSplit;
  periodo: PeriodoLucro;
  anterior: PeriodoRef | null;
  mediaConn: boolean;
  range: { start: string; end: string };
}): BucketKpi[] {
  const rotulos = bucketsDoPeriodo(range);
  const n = rotulos.length;

  const aggCur = aggDe(profitPeriod({ waterfall, periodo, anterior, ads, mediaConn }), mediaConn);
  const aggAnt = anterior
    ? aggDe(
        profitPeriod({
          waterfall,
          periodo: {
            label: anterior.label,
            receita: anterior.receita,
            lucro: anterior.lucro,
            anterior,
          },
          anterior: null,
          ads,
          mediaConn,
        }),
        mediaConn,
      )
    : null;

  const dist = (a: KpiValores) => ({
    vendas: distribuir(a.vendas, n),
    custos: distribuir(a.custos, n),
    marketing: distribuir(a.marketing, n),
    lucro: distribuir(a.lucro, n),
  });
  const dc = dist(aggCur);
  const dp = aggAnt ? dist(aggAnt) : null;

  return rotulos.map((rotulo, i) => {
    const cur: KpiValores = {
      vendas: dc.vendas[i] ?? 0,
      custos: dc.custos[i] ?? 0,
      marketing: dc.marketing[i] ?? 0,
      lucro: dc.lucro[i] ?? 0,
    };
    const prev: KpiValores | null = dp
      ? {
          vendas: dp.vendas[i] ?? 0,
          custos: dp.custos[i] ?? 0,
          marketing: dp.marketing[i] ?? 0,
          lucro: dp.lucro[i] ?? 0,
        }
      : null;
    return {
      rotulo,
      ...cur,
      anterior: prev,
      deltas: prev
        ? {
            vendas: relativeDelta(cur.vendas, prev.vendas),
            custos: relativeDelta(cur.custos, prev.custos),
            marketing: relativeDelta(cur.marketing, prev.marketing),
            lucro: relativeDelta(cur.lucro, prev.lucro),
          }
        : null,
    };
  });
}
